import type { ReactNode } from 'react'
import React, { useMemo, useState } from 'react'
import { ScrollView, View } from 'react-native'
import { tv } from 'tailwind-variants'

import { CoverageBenefits } from '@flows-ui/components/quote/CoverageBenefits'
import { CoverageInformation } from '@flows-ui/components/quote/CoverageInformation'
import { Header } from '@flows-ui/components/quote/Header'
import { Loader } from '@flows-ui/components/quote/Loader'
import type { StepQuoteProps } from '@flows-ui/components/quote/StepQuote'
import { useCoveragePlanSelection, useQuoteLoader } from '@flows-ui/components/quote/hooks'
import { useQuoteStepViewedTracking } from '@flows-ui/components/quote/hooks/useQuoteStepViewedTracking'
import { useQuoteData } from '@flows-ui/services/QuoteDataContext'
import { useTranslation } from '@flows-ui/utils/i18n'
import type { CoverageInformationItem } from '@flows/index'
import { Popup } from '@ui-kit/components/Popup'

import type { CoverageBreakdownRow } from './CoverageBreakdown'
import { CoverageBreakdown } from './CoverageBreakdown'
import { OfferTabs } from './OfferTabs'

const styles = tv({
  slots: {
    container: 'mx-auto w-full flex-col gap-8',
    header: 'w-full self-center sm:max-w-[350px] md:max-w-[449px]',
    sections: 'w-full flex-col gap-8 self-center md:w-[649px]',
  },
})

export type DentalQuoteProps = StepQuoteProps & {
  renderRichContent?: (blocks: unknown[]) => ReactNode
}

export const DentalQuote = (props: DentalQuoteProps) => {
  const showLoader = useQuoteLoader(props.showPriceCalculationLoader)
  const { productGeneration } = useQuoteData()
  const { t } = useTranslation()
  const [isDetailsOpen, setIsDetailsOpen] = useState(false)

  useQuoteStepViewedTracking(props.product)

  const coveragePlans = useMemo(() => productGeneration?.coveragePlans ?? [], [productGeneration])

  const { selectedPlan, selectCoveragePlan } = useCoveragePlanSelection({
    productKey: props.product,
    coveragePlans,
    defaultCoveragePlan: productGeneration?.defaultCoveragePlan ?? null,
  })

  const coveragePlanHeader = productGeneration?.coveragePlanHeader
  const coverageBreakdownDetails = productGeneration?.coverageBreakdownDetails

  const rows = useMemo<CoverageBreakdownRow[]>(() => {
    const planRow: CoverageBreakdownRow[] =
      selectedPlan && coveragePlanHeader
        ? [
            {
              key: 'coverage-plan',
              title: coveragePlanHeader.title,
              description: coveragePlanHeader.description,
              tooltip: {
                title: t('sharedFlows_dental:coverageBreakdown.planTooltip.title'),
                description: t('sharedFlows_dental:coverageBreakdown.planTooltip.description', {
                  selectedTariff: selectedPlan.title,
                }),
              },
              rateSelector: (
                <OfferTabs
                  options={coveragePlans.map((plan) => ({
                    value: plan.itemKey?.current ?? plan._id,
                    label: plan.title,
                  }))}
                  value={selectedPlan.itemKey?.current ?? selectedPlan._id}
                  onValueChange={(value) => {
                    const plan = coveragePlans.find(
                      (candidate) => (candidate.itemKey?.current ?? candidate._id) === value
                    )
                    if (plan) {
                      selectCoveragePlan(plan)
                    }
                  }}
                  helperText={coveragePlanHeader.tagline}
                />
              ),
            },
          ]
        : []

    const breakdownRows: CoverageBreakdownRow[] = (selectedPlan?.coverageBreakdown ?? []).map(
      (item) => ({
        key: item._key,
        title: item.title,
        description: item.description,
        tooltip: item.tooltip,
        fixedRateValue: item.offer?.value,
      })
    )

    return [...planRow, ...breakdownRows]
  }, [coveragePlanHeader, coveragePlans, selectCoveragePlan, selectedPlan, t])

  if (showLoader) {
    return <Loader />
  }

  const { container, header, sections } = styles()

  const renderInfoContent = (item: CoverageInformationItem) =>
    item.webContent && props.renderRichContent
      ? props.renderRichContent(item.webContent)
      : undefined

  return (
    <View className={container()}>
      <View className={header()}>
        <Header
          productName={productGeneration?.titleShort ?? ''}
          headerTitle={props.headerTitle}
          headerSubtitle={props.headerSubtitle}
          productKey={productGeneration?.productKey ?? props.product}
          allowedPremiumIntervals={props.allowedPremiumIntervals}
        />
      </View>

      <View className={sections()}>
        <CoverageBenefits benefits={productGeneration?.coverageBenefits ?? []} />

        <CoverageBreakdown
          title={t('sharedFlows_dental:coverageBreakdown.title')}
          subtitle={t('sharedFlows_dental:coverageBreakdown.subtitle')}
          showDetailsTitle={coverageBreakdownDetails?.title}
          onShowDetails={() => setIsDetailsOpen(true)}
          rows={rows}
        />

        <CoverageInformation
          items={productGeneration?.coverageInformation ?? []}
          renderContent={renderInfoContent}
        />
      </View>

      {coverageBreakdownDetails?.webContent && props.renderRichContent && (
        <Popup visible={isDetailsOpen} onRequestClose={() => setIsDetailsOpen(false)} isFullHeight>
          <ScrollView>{props.renderRichContent(coverageBreakdownDetails.webContent)}</ScrollView>
        </Popup>
      )}
    </View>
  )
}
